import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Button from '../../common/Button';
import FarmacoAutocomplete from '../../common/FarmacoAutocomplete';
import PrescriptionService from '../../../services/prescriptionService';
import PatientService from '../../../services/patientService';
import { formatDateForInput, formatDateEs, getPrescriptionStatusLabel } from '../../../utils';
import './EditPrescription.css';

const EditPrescription = () => {
  const { patientId, prescriptionId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [patient, setPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [formData, setFormData] = useState({
    generalNotes: '',
    nextCheckupDate: '',
    medications: []
  });

  const [originalPrescription, setOriginalPrescription] = useState(null);

  useEffect(() => {
    if (hasLoadedOnce) return;
    
    const loadData = async () => {
      const token = auth.user?.access_token;
      
      if (!token) {
        setIsLoadingData(false);
        setHasLoadedOnce(true);
        return;
      }
      
      try {
        setIsLoadingData(true);
        
        // Load patient
        const patientData = await PatientService.getPatientById(patientId, token);
        setPatient(patientData);
        
        // Load prescription
        const response = await PrescriptionService.getPrescriptionById(prescriptionId, token);
        const prescription = response.data;
        
        setOriginalPrescription(prescription);
        
        // Check if prescription has been printed
        if (prescription.hasBeenPrinted) {
          setError('Esta prescripción ha sido impresa y no puede editarse en el sistema');
          setIsLoadingData(false);
          setHasLoadedOnce(true);
          return;
        }
        
        // Check if prescription has been cancelled
        if (prescription.status === 'cancelled') {
          setError('Esta prescripción ha sido cancelada y no puede editarse');
          setIsLoadingData(false);
          setHasLoadedOnce(true);
          return;
        }
        
        // Transform medications to include farmaco objects for the autocomplete
        const transformedMedications = (prescription.medications || []).map(medication => ({
          ...medication,
          farmaco: medication.farmaco || null,
          farmacoId: medication.farmacoId || ''
        }));
        
        setFormData({
          generalNotes: prescription.generalNotes || '',
          nextCheckupDate: prescription.nextCheckupDate ? formatDateForInput(prescription.nextCheckupDate) : '',
          medications: transformedMedications
        });
        
        setIsLoadingData(false);
        setHasLoadedOnce(true);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Error al cargar la información');
        setIsLoadingData(false);
        setHasLoadedOnce(true);
      }
    };
    
    loadData();
  }, [hasLoadedOnce, patientId, prescriptionId, auth.user?.access_token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((medication, i) => 
        i === index ? { ...medication, [field]: value } : medication
      )
    }));
  };

  const handleFarmacoSelect = (index, farmaco) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) => 
        i === index ? { 
          ...med, 
          farmaco: farmaco,
          farmacoId: farmaco ? farmaco.id : ''
        } : med
      )
    }));
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [
        ...prev.medications,
        {
          farmaco: null,
          farmacoId: '',
          periodicity: '',
          duration: '',
          notes: ''
        }
      ]
    }));
  };

  const removeMedication = (index) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const errors = [];

    if (formData.medications.length === 0) {
      errors.push('Debe agregar al menos un medicamento');
    }

    formData.medications.forEach((medication, index) => {
      if (!medication.farmaco || !medication.farmacoId) {
        errors.push(`Medicamento ${index + 1}: Debe seleccionar un fármaco`);
      }
      if (!medication.periodicity.trim()) {
        errors.push(`Medicamento ${index + 1}: La posología es requerida`);
      }
      if (!medication.duration.trim()) {
        errors.push(`Medicamento ${index + 1}: La duración es requerida`);
      }
    });

    if (formData.nextCheckupDate) {
      const checkupDate = new Date(formData.nextCheckupDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (checkupDate <= today) {
        errors.push('La fecha de la próxima cita debe ser posterior a hoy');
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setMessage({
        type: 'error',
        text: validationErrors.join(', ')
      });
      return;
    }

    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = auth.user?.access_token;
      
      const prescriptionData = {
        generalNotes: formData.generalNotes.trim(),
        nextCheckupDate: formData.nextCheckupDate || null,
        medications: formData.medications.map(medication => ({
          farmacoId: medication.farmacoId,
          farmaco: medication.farmaco ? {
            id: medication.farmaco.id,
            components: medication.farmaco.components,
            dosis: medication.farmaco.dosis,
            commercialName: medication.farmaco.commercialName,
            administrationForm: medication.farmaco.administrationForm
          } : null,
          periodicity: medication.periodicity.trim(),
          duration: medication.duration.trim(),
          notes: medication.notes.trim()
        }))
      };

      await PrescriptionService.updatePrescription(prescriptionId, prescriptionData, token);
      
      setMessage({
        type: 'success',
        text: 'Prescripción actualizada exitosamente'
      });

      // Redirect after a short delay
      setTimeout(() => {
        navigate(`/patients/${patientId}/prescriptions/${prescriptionId}`);
      }, 500);

    } catch (error) {
      console.error('Error updating prescription:', error);
      setMessage({
        type: 'error',
        text: error.message || 'Error al actualizar la prescripción'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/patients/${patientId}/prescriptions`);
  }

  const handleCancel = () => {
    navigate(`/patients/${patientId}/prescriptions/${prescriptionId}`);
  };

  if (error && !patient) {
    return (
      <div className="edit-prescription-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
          <Button variant="outline" onClick={handleCancel}>
            <i className="fas fa-arrow-left"></i>
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-prescription-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Editar Prescripción"
        onBackClick={handleBack}
        backButton={
          <Button variant="outline" onClick={handleCancel}>
            <i className="fas fa-arrow-left"></i>
            Cancelar
          </Button>
        }
      />

      {isLoadingData ? (
        <div className="prescriptions-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      ) : (
        <div className="edit-prescription-content">
          <form onSubmit={handleSubmit} className="prescription-form">
            {message.text && (
              <div className={`message ${message.type}`}>
                <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'}`}></i>
                <span>{message.text}</span>
              </div>
            )}

            <div className="prescription-header">
              <div className="prescription-basic-info">
                <h2>{formatDateEs(originalPrescription.date)}</h2>
                <div className="doctor-info">
                  <i className="fas fa-user-md"></i>
                  <span>Dr. {originalPrescription.doctorName}</span>
                </div>
                <div className="prescription-id">
                  <i className="fas fa-barcode"></i>
                  <span>ID: {originalPrescription.id}</span>
                </div>
              </div>
              <div className="prescription-status">
                <span className={`status-badge status-${originalPrescription.status}`}>
                  {getPrescriptionStatusLabel(originalPrescription.status)}
                </span>
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h3>Medicamentos</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="small"
                  icon="fas fa-plus"
                  onClick={addMedication}
                >
                  Agregar Medicamento
                </Button>
              </div>

              {formData.medications.length === 0 ? (
                <div className="no-medications">
                  <i className="fas fa-pills"></i>
                  <p>No hay medicamentos agregados</p>
                  <Button 
                    type="button" 
                    variant="primary" 
                    onClick={addMedication}
                    icon="fas fa-plus"
                  >
                    Agregar Primer Medicamento
                  </Button>
                </div>
              ) : (
                <div className="medications-list">
                  {formData.medications.map((medication, index) => (
                    <div key={index} className="medication-form-item">
                      <div className="medication-header">
                        <h4>Medicamento {index + 1}</h4>
                        {formData.medications.length > 1 && (
                          <Button
                            type="button"
                            variant="danger"
                            size="small"
                            icon="fas fa-trash"
                            onClick={() => removeMedication(index)}
                          >
                            Eliminar
                          </Button>
                        )}
                      </div>

                      <div className="medication-form-grid">
                        <div className="form-group">
                          <label htmlFor={`farmaco-${index}`}>Fármaco</label>
                          <FarmacoAutocomplete
                            selectedFarmaco={medication.farmaco}
                            onFarmacoSelect={(farmaco) => handleFarmacoSelect(index, farmaco)}
                            placeholder="Seleccionar fármaco"
                            searchPlaceholder="Buscar fármaco por nombre..."
                            disabled={isLoading}
                            minSearchLength={2}
                            maxResults={50}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`periodicity-${index}`}>Periodicidad</label>
                          <input
                            type="text"
                            id={`periodicity-${index}`}
                            value={medication.periodicity}
                            onChange={(e) => handleMedicationChange(index, 'periodicity', e.target.value)}
                            placeholder="Ej: 3 veces al día, cada 8 horas"
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor={`duration-${index}`}>Duración</label>
                          <input
                            type="text"
                            id={`duration-${index}`}
                            value={medication.duration}
                            onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                            placeholder="Ej: 7 días, 2 semanas"
                            required
                          />
                        </div>

                        <div className="form-group full-width">
                          <label htmlFor={`med-notes-${index}`}>Notas del medicamento</label>
                          <textarea
                            id={`med-notes-${index}`}
                            value={medication.notes}
                            onChange={(e) => handleMedicationChange(index, 'notes', e.target.value)}
                            placeholder="Instrucciones especiales, advertencias, etc."
                            rows="3"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {formData.medications.length > 1 && (
                    <div className="section-footer" style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '1rem' }}>
                      <Button
                        type="button"
                        variant="outline"
                        size="small"
                        icon="fas fa-plus"
                        onClick={addMedication}
                      >
                        Agregar Medicamento
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

             <div className="form-section">
              <h3>
                <i className="fas fa-sticky-note"></i>
                Notas Generales (Opcional)
              </h3>
              <div className="form-group">
                <textarea
                  id="generalNotes"
                  name="generalNotes"
                  value={formData.generalNotes}
                  onChange={handleInputChange}
                  placeholder="Ingrese el diagnóstico o notas generales sobre la prescripción..."
                  rows="4"
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>
                <i className="fas fa-calendar-check"></i>
                Próxima Cita de Control
              </h3>
              <div className="form-group">
                <label htmlFor="nextCheckupDate">Fecha de Próxima Cita</label>
                <input
                  type="date"
                  id="nextCheckupDate"
                  name="nextCheckupDate"
                  value={formData.nextCheckupDate}
                  onChange={handleInputChange}
                  min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  className="form-control"
                  disabled={true}
                />
                <small className="form-help">
                  Para modificar la fecha de seguimiento, por favor edite la cita correspondiente en el calendario.
                </small>
              </div>
            </div>

            <div className="form-actions">
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary" 
                disabled={isLoading}
                icon={isLoading ? "fas fa-spinner fa-spin" : "fas fa-save"}
              >
                {isLoading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EditPrescription;
